import { Reducer, useCallback, useEffect, useReducer, useState } from 'react';
import isFunction from 'lodash/isFunction';

import { SafeAnyType } from 'src/utils/safeAny';
import { ApiError, useRequest } from 'src/utils/hooks/useRequest';
import { BaseStoreEntityType, baseStoreEntityValue } from 'src/store/types';
import { PromiseReturnType } from 'src/utils/types';
import { useMemoObject } from 'src/utils/hooks/useMemoObject';

type ActonType =
  | { type: 'start' }
  | { type: 'ok'; payload: SafeAnyType }
  | { type: 'change'; payload: SafeAnyType }
  | { type: 'error'; payload: ApiError }
  | { type: 'finish' };

type ReducerType<R = SafeAnyType> = Reducer<BaseStoreEntityType<R, ApiError>, ActonType>;

const reducer: ReducerType = (state, action) => {
  switch (action.type) {
    case 'start':
      return baseStoreEntityValue(null, true);
    case 'ok':
      return baseStoreEntityValue(action.payload);
    case 'change':
      return baseStoreEntityValue(state.response ? { ...state.response, ...action.payload } : action.payload);
    case 'error':
      return baseStoreEntityValue(null, false, action.payload);
    case 'finish':
      return { ...state, loading: false };
    default:
      return state;
  }
};

type ReturnType<R extends SafeAnyType, F extends (...args: SafeAnyType) => SafeAnyType> = {
  apiCaller: (data?: Parameters<F>[0]) => Promise<PromiseReturnType<F>>;
  abort: () => void;
  changeResponse: (data: Partial<R>) => void;
  errorTextList: string[];
  clearErrors: () => void;
  addErrors: (errors: string[]) => void;
} & BaseStoreEntityType<R, ApiError>;

type ConfigProps<R extends SafeAnyType, F extends (...args: SafeAnyType) => SafeAnyType> = {
  data?: Parameters<F>[0];
  showErrors?: boolean;
  saveResponse?: boolean;
  initialCall?: boolean;
  onSuccess?: (response: R) => void;
  onError?: (error: ApiError) => void;
  onFinally?: () => void;
};

export const useOneRequest = <
  R extends SafeAnyType = SafeAnyType,
  F extends (...args: SafeAnyType) => SafeAnyType = (...args: SafeAnyType) => SafeAnyType
>(
  apiFunction: F,
  { data, showErrors, initialCall, saveResponse, onSuccess, onError, onFinally }: ConfigProps<R, F> = {}
): ReturnType<R, F> => {
  const { apiCaller, abort } = useRequest();

  const [errorTextList, setErrorTextList] = useState<string[]>([]);
  /** errors specific to the request */
  const [state, dispatch] = useReducer<ReducerType<R>>(reducer, baseStoreEntityValue<R, ApiError>(null, !!initialCall));

  const addErrors = useCallback((errors: string[]) => {
    setErrorTextList((prevState) => [...new Set([...prevState, ...errors])]);
  }, []);

  const clearErrors = useCallback(() => {
    setErrorTextList([]);
  }, []);

  const abortLocal = useCallback(() => {
    dispatch({ type: 'finish' });

    abort();
  }, [abort]);

  const changeResponse = useCallback((payload: Partial<R>) => dispatch({ type: 'change', payload }), []);

  const memoizedData = useMemoObject(data);

  const apiCallerLocal = useCallback(
    (apiData: Parameters<F>[0] = memoizedData) => {
      abort();

      dispatch({ type: 'start' });

      clearErrors();

      return apiCaller(apiFunction, apiData, { showErrors })
        .then((response) => {
          if (initialCall || saveResponse) {
            dispatch({ type: 'ok', payload: response });
          }

          if (isFunction(onSuccess)) {
            onSuccess(response);
          }

          return response;
        })
        .catch((error: ApiError) => {
          if (initialCall || saveResponse) {
            dispatch({ type: 'error', payload: error });
          }

          setErrorTextList(error.errorMessages);

          if (isFunction(onError)) {
            onError(error);
          }

          throw error;
        })
        .finally(() => {
          if (isFunction(onFinally)) {
            onFinally();
          }

          dispatch({ type: 'finish' });
        });
    },
    // eslint-disable-next-line
    [apiCaller, abort, apiFunction, initialCall, saveResponse, showErrors, clearErrors, memoizedData]
  );

  useEffect(() => {
    if (initialCall) {
      apiCallerLocal();
    }
  }, [apiCallerLocal, initialCall]);

  return {
    apiCaller: apiCallerLocal,
    abort: abortLocal,
    loading: state.loading,
    response: state.response,
    error: state.error,
    changeResponse,
    errorTextList,
    clearErrors,
    addErrors,
  };
};
